import { getOpenAIClient, isOpenAIConfigured } from './openaiClient';

/**
 * Career Advancement Service
 * Provides AI-powered guidance for promotion and salary advancement timing
 */

/**
 * Analyzes career advancement readiness and timing
 * @param {Object} userData - User's career profile and current situation
 * @returns {Promise<Object>} Career advancement guidance and recommendations
 */
export async function generateAdvancementGuidance(userData) {
  try {
    if (!isOpenAIConfigured()) {
      throw new Error('OpenAI API is not properly configured. Please check your VITE_OPENAI_API_KEY environment variable.');
    }

    const openai = getOpenAIClient();
    const { 
      occupation, 
      seniority, 
      currentSalary, 
      lastPromotion, 
      lastRaise, 
      currentPerformance,
      marketConditions,
      companySize,
      tenure,
      achievements = [],
      skillsAcquired = [],
      userId 
    } = userData;

    const systemPrompt = `You are an expert career advancement strategist specializing in promotion and salary negotiation timing. Analyze the user's career profile and provide strategic guidance on when and how to ask for promotions or raises. Focus on data-driven timing recommendations, market analysis, and actionable strategies.

Your guidance should be professional, strategic, and based on industry best practices. Consider market timing, performance indicators, and optimal opportunity windows.`;

    const userPrompt = `Analyze this career profile and provide advancement guidance:

Career Profile:
- Occupation: ${occupation}
- Current Level: ${seniority}
- Current Salary: ${currentSalary || 'Not specified'}
- Last Promotion: ${lastPromotion || 'Not specified'}  
- Last Raise: ${lastRaise || 'Not specified'}
- Current Performance: ${currentPerformance || 'Good'}
- Company Size: ${companySize || 'Medium'}
- Tenure: ${tenure || 'Not specified'}
- Recent Achievements: ${achievements?.length > 0 ? achievements?.join(', ') : 'None specified'}
- Skills Acquired: ${skillsAcquired?.length > 0 ? skillsAcquired?.join(', ') : 'None specified'}
- Market Conditions: ${marketConditions || 'Stable'}

Provide strategic guidance on advancement timing and approach.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      reasoning_effort: 'high',
      verbosity: 'high',
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'career_advancement_guidance',
          schema: {
            type: 'object',
            properties: {
              readinessScore: { 
                type: 'number', 
                minimum: 0, 
                maximum: 100,
                description: 'Overall advancement readiness score (0-100)'
              },
              timingRecommendation: {
                type: 'object',
                properties: {
                  promotion: {
                    type: 'object',
                    properties: {
                      recommended: { type: 'boolean' },
                      timeframe: { type: 'string' },
                      confidence: { type: 'number', minimum: 0, maximum: 1 },
                      reasoning: { type: 'string' }
                    },
                    required: ['recommended', 'timeframe', 'confidence', 'reasoning']
                  },
                  salaryIncrease: {
                    type: 'object',
                    properties: {
                      recommended: { type: 'boolean' },
                      timeframe: { type: 'string' },
                      suggestedIncrease: { type: 'string' },
                      confidence: { type: 'number', minimum: 0, maximum: 1 },
                      reasoning: { type: 'string' }
                    },
                    required: ['recommended', 'timeframe', 'confidence', 'reasoning']
                  }
                },
                required: ['promotion', 'salaryIncrease']
              },
              strategicGuidance: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    category: { type: 'string' },
                    title: { type: 'string' },
                    description: { type: 'string' },
                    actionItems: { type: 'array', items: { type: 'string' } },
                    priority: { type: 'string', enum: ['high', 'medium', 'low'] }
                  },
                  required: ['category', 'title', 'description', 'actionItems', 'priority']
                }
              },
              marketInsights: {
                type: 'object',
                properties: {
                  industryTrends: { type: 'string' },
                  salaryBenchmarks: { type: 'string' },
                  demandIndicators: { type: 'string' },
                  timingFactors: { type: 'string' }
                },
                required: ['industryTrends', 'salaryBenchmarks', 'demandIndicators', 'timingFactors']
              },
              preparationPlan: {
                type: 'object',
                properties: {
                  immediateActions: { type: 'array', items: { type: 'string' } },
                  shortTermGoals: { type: 'array', items: { type: 'string' } },
                  longTermStrategy: { type: 'array', items: { type: 'string' } },
                  negotiationTips: { type: 'array', items: { type: 'string' } }
                },
                required: ['immediateActions', 'shortTermGoals', 'longTermStrategy', 'negotiationTips']
              }
            },
            required: ['readinessScore', 'timingRecommendation', 'strategicGuidance', 'marketInsights', 'preparationPlan'],
            additionalProperties: false
          }
        }
      }
    });

    const guidanceData = JSON.parse(response?.choices?.[0]?.message?.content);
    
    return {
      id: `advancement_guidance_${Date.now()}`,
      userId: userId,
      createdAt: new Date(),
      lastUpdated: new Date(),
      ...guidanceData
    };
  } catch (error) {
    console.error('Error generating advancement guidance:', error);
    
    if (error?.message?.includes('not properly configured')) {
      throw error;
    } else if (error?.status === 401) {
      throw new Error('Invalid OpenAI API key. Please check your VITE_OPENAI_API_KEY environment variable.');
    } else if (error?.status === 429) {
      throw new Error('OpenAI API quota exceeded. Please check your usage limits.');
    } else {
      throw new Error('Failed to generate advancement guidance. Please try again later.');
    }
  }
}

/**
 * Generates negotiation scenarios and scripts
 * @param {Object} negotiationContext - Context for the negotiation scenario
 * @returns {Promise<Object>} Negotiation scripts and strategies
 */
export async function generateNegotiationScenarios(negotiationContext) {
  try {
    if (!isOpenAIConfigured()) {
      throw new Error('OpenAI API is not properly configured. Please check your VITE_OPENAI_API_KEY environment variable.');
    }

    const openai = getOpenAIClient();
    const { 
      type, // 'promotion' or 'raise'
      currentRole, 
      targetRole, 
      currentSalary, 
      targetSalary,
      justifications = [],
      managerType,
      companyBudget,
      userId 
    } = negotiationContext;

    const systemPrompt = `You are an expert negotiation coach specializing in salary and promotion negotiations. Generate realistic negotiation scenarios, scripts, and strategies tailored to the user's specific situation. Focus on professional, diplomatic approaches that maximize success probability.

Provide practical scripts and handle common objections professionally.`;

    const userPrompt = `Create negotiation scenarios for this situation:

Negotiation Type: ${type}
Current Role: ${currentRole}
${type === 'promotion' ? `Target Role: ${targetRole}` : ''}
Current Salary: ${currentSalary || 'Not specified'}
${type === 'raise' ? `Target Salary: ${targetSalary || 'Market rate + 10-20%'}` : ''}
Key Justifications: ${justifications?.join(', ') || 'Performance-based'}
Manager Style: ${managerType || 'Professional'}
Company Budget Situation: ${companyBudget || 'Standard'}

Generate comprehensive negotiation guidance with realistic scenarios and scripts.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      reasoning_effort: 'medium',
      verbosity: 'high',
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'negotiation_scenarios',
          schema: {
            type: 'object',
            properties: {
              negotiationType: { type: 'string' },
              openingScripts: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    scenario: { type: 'string' },
                    script: { type: 'string' },
                    tone: { type: 'string' },
                    effectiveness: { type: 'string' }
                  },
                  required: ['scenario', 'script', 'tone', 'effectiveness']
                }
              },
              objectionHandling: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    objection: { type: 'string' },
                    response: { type: 'string' },
                    followUp: { type: 'string' }
                  },
                  required: ['objection', 'response', 'followUp']
                }
              },
              negotiationStrategy: {
                type: 'object',
                properties: {
                  preparation: { type: 'array', items: { type: 'string' } },
                  timing: { type: 'string' },
                  alternatives: { type: 'array', items: { type: 'string' } },
                  walkAwayPoint: { type: 'string' }
                },
                required: ['preparation', 'timing', 'alternatives', 'walkAwayPoint']
              },
              successPredictors: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    factor: { type: 'string' },
                    importance: { type: 'string', enum: ['high', 'medium', 'low'] },
                    description: { type: 'string' }
                  },
                  required: ['factor', 'importance', 'description']
                }
              }
            },
            required: ['negotiationType', 'openingScripts', 'objectionHandling', 'negotiationStrategy', 'successPredictors'],
            additionalProperties: false
          }
        }
      }
    });

    const scenarioData = JSON.parse(response?.choices?.[0]?.message?.content);
    
    return {
      id: `negotiation_scenarios_${Date.now()}`,
      userId: userId,
      createdAt: new Date(),
      ...scenarioData
    };
  } catch (error) {
    console.error('Error generating negotiation scenarios:', error);
    
    if (error?.message?.includes('not properly configured')) {
      throw error;
    } else if (error?.status === 401) {
      throw new Error('Invalid OpenAI API key. Please check your VITE_OPENAI_API_KEY environment variable.');
    } else {
      throw new Error('Failed to generate negotiation scenarios. Please try again later.');
    }
  }
}

/**
 * Calculates advancement probability based on various factors
 * @param {Object} factors - Factors affecting advancement probability
 * @returns {Promise<Object>} Probability analysis and recommendations
 */
export async function calculateAdvancementProbability(factors) {
  try {
    if (!isOpenAIConfigured()) {
      throw new Error('OpenAI API is not properly configured. Please check your VITE_OPENAI_API_KEY environment variable.');
    }

    const openai = getOpenAIClient();
    const { 
      performanceRating,
      tenure,
      marketConditions,
      budgetCycle,
      recentAchievements,
      skillGaps,
      companyGrowth,
      competitionLevel,
      userId 
    } = factors;

    const systemPrompt = `You are a data-driven career analytics expert. Analyze the provided factors to calculate advancement probability and provide strategic insights. Use statistical reasoning and industry benchmarks to provide accurate probability assessments.

Focus on quantitative analysis while providing actionable recommendations to improve success odds.`;

    const userPrompt = `Calculate advancement probability based on these factors:

Performance Rating: ${performanceRating || 'Good'}
Tenure: ${tenure || '1-2 years'}
Market Conditions: ${marketConditions || 'Stable'}
Budget Cycle Phase: ${budgetCycle || 'Mid-cycle'}
Recent Achievements: ${recentAchievements?.join(', ') || 'Standard performance'}
Skill Gaps: ${skillGaps?.join(', ') || 'None identified'}
Company Growth: ${companyGrowth || 'Stable'}
Internal Competition: ${competitionLevel || 'Moderate'}

Provide detailed probability analysis with improvement strategies.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      reasoning_effort: 'high',
      verbosity: 'medium',
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'advancement_probability',
          schema: {
            type: 'object',
            properties: {
              probabilityScore: { 
                type: 'number', 
                minimum: 0, 
                maximum: 100,
                description: 'Overall advancement probability (0-100%)'
              },
              factorAnalysis: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    factor: { type: 'string' },
                    impact: { type: 'string', enum: ['positive', 'negative', 'neutral'] },
                    weight: { type: 'number', minimum: 0, maximum: 1 },
                    description: { type: 'string' }
                  },
                  required: ['factor', 'impact', 'weight', 'description']
                }
              },
              improvementStrategies: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    strategy: { type: 'string' },
                    potentialImpact: { type: 'string' },
                    timeframe: { type: 'string' },
                    effort: { type: 'string', enum: ['low', 'medium', 'high'] }
                  },
                  required: ['strategy', 'potentialImpact', 'timeframe', 'effort']
                }
              },
              bestTimeframes: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    period: { type: 'string' },
                    probability: { type: 'number', minimum: 0, maximum: 100 },
                    reasoning: { type: 'string' }
                  },
                  required: ['period', 'probability', 'reasoning']
                }
              },
              riskFactors: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    risk: { type: 'string' },
                    severity: { type: 'string', enum: ['low', 'medium', 'high'] },
                    mitigation: { type: 'string' }
                  },
                  required: ['risk', 'severity', 'mitigation']
                }
              }
            },
            required: ['probabilityScore', 'factorAnalysis', 'improvementStrategies', 'bestTimeframes', 'riskFactors'],
            additionalProperties: false
          }
        }
      }
    });

    const probabilityData = JSON.parse(response?.choices?.[0]?.message?.content);
    
    return {
      id: `probability_analysis_${Date.now()}`,
      userId: userId,
      createdAt: new Date(),
      ...probabilityData
    };
  } catch (error) {
    console.error('Error calculating advancement probability:', error);
    
    if (error?.message?.includes('not properly configured')) {
      throw error;
    } else if (error?.status === 401) {
      throw new Error('Invalid OpenAI API key. Please check your VITE_OPENAI_API_KEY environment variable.');
    } else {
      throw new Error('Failed to calculate advancement probability. Please try again later.');
    }
  }
}

export default {
  generateAdvancementGuidance,
  generateNegotiationScenarios,
  calculateAdvancementProbability
};